import { Component } from '@theme/component';

/**
 * Copies a product discount code to the clipboard and shows inline feedback.
 * Reconnects cleanly after the price container is re-rendered by variant updates.
 *
 * @typedef {Object} CopyDiscountCodeRefs
 * @property {HTMLElement} [codeText]
 * @property {HTMLElement} [codeValue]
 * @property {HTMLElement} [statusText]
 *
 * @extends {Component<CopyDiscountCodeRefs>}
 */
class CopyDiscountCode extends Component {
  /** @type {number | undefined} */
  #feedbackTimeout;

  /** @type {string | undefined} */
  #originalText;

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#feedbackTimeout);
  }

  /**
   * Copies the discount code to the clipboard.
   */
  copyCode = async () => {
    const { code } = this.dataset;
    if (!code) return;

    try {
      await this.#writeClipboard(code);
      this.#showFeedback();
    } catch {
      this.#selectCodeText();
    }
  };

  /**
   * Writes text to the clipboard, falling back to a hidden textarea when the
   * async clipboard API is unavailable or rejects (insecure context, no focus).
   *
   * @param {string} text - The text to copy.
   */
  async #writeClipboard(text) {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch {
        // Fall through to the textarea fallback below.
      }
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.append(textarea);
    textarea.select();

    const copied = document.execCommand('copy');
    textarea.remove();

    if (!copied) {
      throw new Error('Copy command was rejected');
    }
  }

  /**
   * Swaps the code text for a confirmation message, then restores it.
   */
  #showFeedback() {
    const { codeText, statusText } = this.refs;
    if (!(codeText instanceof HTMLElement) || !(statusText instanceof HTMLElement)) return;

    if (this.#originalText === undefined) {
      this.#originalText = codeText.textContent ?? '';
    }

    window.clearTimeout(this.#feedbackTimeout);
    const copiedText = this.dataset.copiedText ?? 'Copied';
    codeText.textContent = copiedText;
    statusText.textContent = copiedText;

    this.#feedbackTimeout = window.setTimeout(() => {
      codeText.textContent = this.#originalText ?? '';
      statusText.textContent = '';
    }, 2000);
  }

  /**
   * Selects the code text so it can be copied manually when clipboard access is denied.
   */
  #selectCodeText() {
    const target = this.refs.codeValue ?? this.refs.codeText;
    if (!target) return;

    const range = document.createRange();
    range.selectNodeContents(target);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }
}

if (!customElements.get('copy-discount-code')) {
  customElements.define('copy-discount-code', CopyDiscountCode);
}
